import { Popover, Skeleton } from 'antd'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import * as com from '../Common.js'
import ChatFeedback from './ChatFeedback'
import './MessageList.css'
import TextSelectionPopup from './TextSelectionPopup'
import { CONTENT_TYPES, IS_WHITE_LIST, PDF_GUIDELINES, PDF_TYPE_NAME, SHOW_CONTACT } from './constants'
import useTextSelection from './useTextSelection.js'
import useScenarioUsed from './hooks/useScenarioUsed'
import DeepSearchThinking from './components/DeepSearchThinking'

import ICON_BOT from './assets/bot.svg'
import ContactRelation from './components/Contact/Relation/index.tsx'
import { IS_IFRAMES } from './constants'
import InitializePanel from './components/Sign/InitializePanel'

// Begin quote icon component for explain messages
const BeginQuoteIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="10"
        height="10"
        viewBox="0 0 10 10"
        fill="none"
    >
        <path
            d="M5.87786 9.5V6.48712C5.87786 4.83906 6.19593 3.48712 6.83206 2.43133C7.49364 1.37554 8.54962 0.73176 10 0.5V2.08369C9.13486 2.18669 8.52417 2.52146 8.16794 3.08798C7.8117 3.65451 7.63359 4.55579 7.63359 5.79184L6.48855 5.63734H9.96183V9.5H5.87786ZM0 9.5V6.48712C0 4.83906 0.318066 3.48712 0.954198 2.43133C1.61578 1.37554 2.67176 0.73176 4.12214 0.5V2.08369C3.257 2.18669 2.64631 2.52146 2.29008 3.08798C1.93384 3.65451 1.75573 4.55579 1.75573 5.79184L0.610687 5.63734H4.08397V9.5H0Z"
            fill="url(#paint0_linear_2809_915)"
        />
        <defs>
            <linearGradient
                id="paint0_linear_2809_915"
                x1="0"
                y1="5"
                x2="10.0061"
                y2="5"
                gradientUnits="userSpaceOnUse"
            >
                <stop offset="0.245186" stopColor="#7E37F6" />
                <stop offset="1" stopColor="#23B6F1" />
            </linearGradient>
        </defs>
    </svg>
)

// End quote icon component for explain messages
const EndQuoteIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="10"
        height="10"
        viewBox="0 0 10 10"
        fill="none"
    >
        <path
            d="M4.12214 0.5V3.51288C4.12214 5.16094 3.80407 6.51288 3.16794 7.56867C2.50636 8.62446 1.45038 9.26824 0 9.5V7.91631C0.86514 7.81331 1.47583 7.47854 1.83206 6.91202C2.1883 6.34549 2.36641 5.44421 2.36641 4.20816L3.51145 4.36266H0.0381689V0.5H4.12214ZM10 0.5V3.51288C10 5.16094 9.68193 6.51288 9.0458 7.56867C8.38422 8.62446 7.32824 9.26824 5.87786 9.5V7.91631C6.743 7.81331 7.35369 7.47854 7.70992 6.91202C8.06616 6.34549 8.24427 5.44421 8.24427 4.20816L9.38931 4.36266H5.91603V0.5H10Z"
            fill="url(#paint0_linear_3808_7685)"
        />
        <defs>
            <linearGradient
                id="paint0_linear_3808_7685"
                x1="10"
                y1="5"
                x2="-0.00608278"
                y2="5"
                gradientUnits="userSpaceOnUse"
            >
                <stop offset="0.245186" stopColor="#7E37F6" />
                <stop offset="1" stopColor="#23B6F1" />
            </linearGradient>
        </defs>
    </svg>
)

// Explain tooltip component for showing full text when truncated
const ExplainTooltip = ({ text, children, isMobile }) => {
    const [isTextTruncated, setIsTextTruncated] = useState(false)
    const [containerWidth, setContainerWidth] = useState(0)
    const textRef = useRef(null)

    useEffect(() => {
        const checkTruncation = () => {
            if (textRef.current) {
                const element = textRef.current
                const container = element.closest('.explain-quote-content')
                if (container) {
                    setContainerWidth(container.offsetWidth)
                }
                setIsTextTruncated(element.scrollWidth > element.clientWidth)
            }
        }

        // Use setTimeout to ensure DOM is updated
        const timeoutId = setTimeout(checkTruncation, 0)

        window.addEventListener('resize', checkTruncation)
        return () => {
            clearTimeout(timeoutId)
            window.removeEventListener('resize', checkTruncation)
        }
    }, [text])

    // Don't show popover on mobile or if text is not truncated
    if (isMobile || !isTextTruncated) {
        return (
            <div ref={textRef} className="explain-quoted-text">
                {text}
            </div>
        )
    }

    return (
        <Popover
            content={text}
            title={null}
            overlayClassName="explain-tooltip-popover"
            styles={{
                body: {
                    display: 'inline-flex',
                    padding: '12px',
                    alignItems: 'stretch',
                    gap: '10px',
                    borderRadius: '8px',
                    background: 'rgba(0, 0, 0, 0.75)',
                    backgroundColor: 'rgba(0, 0, 0, 0.75)',
                    boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.12)',
                    border: 'none',
                    color: '#FFFFFF',
                    fontFamily: 'Inter',
                    fontSize: '14px',
                    fontWeight: '500',
                    lineHeight: '1.2857142857142858em',
                    whiteSpace: 'pre-wrap',
                    width: 'auto',
                    maxWidth:
                        containerWidth > 0 ? `${containerWidth}px` : '400px',
                    wordBreak: 'break-word',
                },
            }}
            placement="bottom"
            autoAdjustOverflow={true}
            mouseEnterDelay={0.5}
            mouseLeaveDelay={0.1}
        >
            <div ref={textRef} className="explain-quoted-text">
                {text}
            </div>
        </Popover>
    )
}

const Message = React.forwardRef(function Message(props, ref) {
    const {
        text,
        index,
        sender,
        programs,
        previewId,
        signupGuide,
        isShareChat = false,
        currentPdfType,
        pdCFontent,
        isMobile = false,
        isScenario = false,
        source = [],
        isProgress = false,
        thinking,
        thinkingStartDate, 
        thinkingEndDate,
        onContinue,
        onToSignUp,
        onSuccess,
        isFannieMaeStandalone,
        isFreddieMacStandalone,
    } = props

    const MAP_GUIDELINE_ID = programs?.data?.MAP_GUIDELINE_ID

    const isGreeting = useMemo(
        () => text === 'type-greeting' || text === 'reset-greeting',
        [text]
    )

    // Check if this is an "explain" question
    const isExplainQuestion = useMemo(() => {
        return (
            sender === 'user' &&
            text.startsWith('Please explain: "') &&
            text.endsWith('"')
        )
    }, [sender, text])

    // Extract the quoted text from explain question
    const explainText = useMemo(() => {
        if (!isExplainQuestion) return null
        const match = text.match(/^Please explain: "(.*)"$/s)
        if (!match) return null

        // Replace multiple consecutive newlines with single newline
        const cleanedText = match[1].replace(/\n{2,}/g, '\n')
        return cleanedText
    }, [isExplainQuestion, text])

    // Check if this is a scenario user message with sources
    const isScenarioQuestion = useMemo(() => {
        return (
            sender === 'user' &&
            isScenario &&
            Array.isArray(source) &&
            source.length > 0
        )
    }, [sender, isScenario, source])

    if (isGreeting) {
        return (
            <div className={`${sender}-message`} ref={ref}>
                <img
                    src={ICON_BOT}
                    alt="Bot Avatar"
                    className="avatar"
                />
                <div className="text-bubble">
                    <div>
                        {sessionStorage.getItem('activeGPTmode') === 'guideline' || isFannieMaeStandalone || isFreddieMacStandalone ? <div>
                            Hello! I'm an AI assistant specialized in the{' '}
                            {text === 'type-greeting'
                                ? PDF_TYPE_NAME[currentPdfType]
                                : pdCFontent}{' '}
                            lending guidelines. How can I help you today?
                        </div> : <div>
                                   {
                                    previewId ? 
                                    "I am a mortgage-focused AI assistant designed to help you with mortgage-related questions and scenarios. How can I assist you today?" : 
                                    "Hello, I am a Scenario AI assistant, how can I help you?"
                                   }
                                </div>}
                    </div>
                </div>
            </div>
        )
    }

    // Render special explain question layout
    if (isExplainQuestion && explainText) {
        return (
            <div className="explain-message-wrapper" ref={ref}>
                {/* First part: "Explain" text in regular user message style */}
                <div
                    className={`${sender}-message`}
                    style={{
                        backgroundColor:
                            sender === 'user' && isShareChat ? '#f6f6f6' : '',
                        marginBottom: '4px', // Add spacing between the two parts
                    }}
                >
                    <div className="text-bubble">Explain</div>
                </div>

                {/* Second part: Quoted text in special component with horizontal layout */}
                <div className="explain-quote-content">
                    <div className="explain-quote-icon">
                        <BeginQuoteIcon />
                    </div>
                    <ExplainTooltip text={explainText} isMobile={isMobile} />
                    <div className="explain-quote-icon">
                        <EndQuoteIcon />
                    </div>
                </div>
            </div>
        )
    }

    // Render special scenario user message layout with sources
    if (isScenarioQuestion) {
        return (
            <div className="scenario-message-wrapper" ref={ref}>
                {/* Main user message */}
                <div
                    className={`${sender}-message`}
                    style={{
                        backgroundColor:
                            sender === 'user' && isShareChat ? '#f6f6f6' : '',
                        marginBottom: '4px',
                    }}
                >
                    <div className="text-bubble">
                        <div>{text}</div>
                    </div>
                </div>

                {/* Sources section - displays source array as tags */}
                <div className="scenario-sources-section">
                    <div className="scenario-sources-label">Sources:</div>
                    <div className="scenario-sources-list">
                        {source.map((sourceItem, index) => (
                            <div key={index} className="scenario-source-tag">
                                {sourceItem}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div
            className={`${sender}-message`}
            style={{
                backgroundColor:
                    sender === 'user' && isShareChat ? '#f6f6f6' : '',
            }}
            ref={ref}
        >
            {sender === 'bot' && (
                <img
                    style={(isProgress && !thinking?.length) || thinking?.length ? { marginTop: '12px' } : {}}
                    src={ICON_BOT}
                    alt="Bot Avatar"
                    className={`avatar ${(!thinking?.length && text !== '...' && !isProgress) || (thinking?.length && thinkingEndDate) || (thinking?.length && !thinkingStartDate && !thinkingEndDate) ? '' : 'rotating'}`}
                />
            )}

           <div>

           {thinking?.length ? <DeepSearchThinking startTime={thinkingStartDate} endTime={thinkingEndDate} content={thinking}  /> : null}

            <div
                style={signupGuide? {height: signupGuide + 550 , overflow:'hidden'} : undefined}
                id={sender === 'bot' ? `chat-${index}` : undefined}
                className={`text-bubble ${
                    text === '...' ? 'typing-indicator' : ''
                } ${(!thinking?.length && isProgress) ? 'progress-message' : ''}`}
            >
                {signupGuide &&  <InitializePanel onSuccess={onSuccess} onContinue={onContinue} onToSignUp={onToSignUp} />}
                {text === '...' ? (
                    <>
                        <span className="dot">.</span>
                        <span className="dot">.</span>
                        <span className="dot">.</span>
                        <span className="dot">.</span>
                        <span className="dot">.</span>
                        <span className="dot">.</span>
                    </>
                ) : isProgress ? (
                    thinking?.length ? null : (<div className="progress-content">
                        <img 
                            src="/images/scenario-thinking.svg" 
                            alt="Loading" 
                            className="progress-icon"
                        />
                        <span className="progress-text">{text.replace('Responsive_Lender_', '')}</span>
                    </div>)
                ) : (
                    <div>{text}</div>
                )}
            </div>
           </div>
        </div>
    )
})

function MessageList({
    messages,
    threadID,
    previewId,
    signupGuide,
    isBotThinking,
    isBotAnswering,
    isReceivingAnswer,
    isProcessing,
    progressMessage,
    currentMessageID,
    currentMessageText,
    loadingChat,
    isMobile,
    setMobileAIChat,
    pdCFontent,
    usage,
    onContinue,
    onToSignUp,
    onSuccess,
    onSendMessage,
    onScenarioSearch,
    isLandingWelcome,
    contact,
    programs,
    isFannieMaeStandalone,
    isFreddieMacStandalone,
}) {
    const messageContainerRef = useRef(null)
    const guidelineWrapperRef = useRef(null)
    const lastQuestionRef = useRef(null)
    const [containerElement, setContainerElement] = useState(null)


    const pdfType = useSelector((state) => state.common.content)
    const source = useSelector((state) => state.common.source) // Get selected guidelines from Redux

    const isScenario = pdfType === CONTENT_TYPES.SCENARIO_GMCC

    const subscriptionReducer = useSelector(
        (state) => state.subscriptionReducer?.currentInfo
    )

    const getFreeStatus = useCallback(() => {
        return com.getFreeStatus(
            subscriptionReducer.plan,
            subscriptionReducer.loanOfficers
        )
    }, [subscriptionReducer.plan, subscriptionReducer.loanOfficers])

    const isFreeLimitReached = useMemo(() => {
        if (isScenario) return false
        return getFreeStatus() && usage >= 5
    }, [getFreeStatus, isScenario, usage])

    // Check if explain popup should be enabled (similar to isButtonActive in ChatInput)
    const isExplainEnabled = useMemo(() => {
        return !isBotThinking && !isReceivingAnswer && !isFreeLimitReached
    }, [isBotThinking, isReceivingAnswer, isFreeLimitReached])

    // Text selection functionality
    const { selectedText, popupPosition, showPopup, closePopup } =
        useTextSelection(containerElement, { isPopupEnabled: isExplainEnabled })

    // TODO: Implement quote feature
    // const handleQuote = useCallback(
    //     (text) => {
    //         const quotedMessage = `"${text}"`
    //         handleSendDocMessage(quotedMessage, PDF_GUIDELINES.FANNIE_MAE)
    //     },
    //     [handleSendDocMessage]
    // )

    const handleExplain = useCallback(
        (text) => {
            const explainMessage = `Please explain: "${text}"`
            if (isScenario) {
                if (onScenarioSearch) {
                    onScenarioSearch(explainMessage)
                }
            } else {
                if (onSendMessage) {
                    onSendMessage(explainMessage, PDF_GUIDELINES.FANNIE_MAE)
                }
            }
        },
        [isScenario, onSendMessage, onScenarioSearch]
    )

    const showFeedback = useMemo(
        () => currentMessageID && !isBotThinking,
        [currentMessageID, isBotThinking]
    )

    const isLastUserMessage = (index) => {
        return (
            messages[index].sender === 'user' &&
            (index === messages.length - 1 ||
                messages.slice(index + 1).every((msg) => msg.sender !== 'user'))
        )
    }

    const showPdf = useSelector((state) => state.common.showPdf)

    const scrollToBottom = () => {
        if (messageContainerRef.current) {
            messageContainerRef.current.scrollTop =
                messageContainerRef.current.scrollHeight
        }
    }

    // 当 "Create a free account to unlock full answer" 弹窗出现时，将右侧回答区域滚动到底部，使弹窗完整可见
    useEffect(() => {
        if (!signupGuide) return
        const id = setTimeout(scrollToBottom, 100)
        return () => clearTimeout(id)
    }, [signupGuide])

    const isQuestionVisible = () => {
        const container = messageContainerRef.current
        const question = lastQuestionRef.current
        if (!container || !question) return true

        const containerRect = container.getBoundingClientRect()
        const questionRect = question.getBoundingClientRect()
        return (
            questionRect.top >= containerRect.top + 30 &&
            questionRect.bottom <= containerRect.bottom
        )
    }

    useEffect(() => {
        if (
            messages.length > 0 &&
            messages[messages.length - 1].sender === 'user'
        ) {
            scrollToBottom()
        }
    }, [messages])

    useEffect(() => {
        if (isBotAnswering && isQuestionVisible()) {
            scrollToBottom()
        }
    }, [messages, isBotAnswering, isQuestionVisible])

    useEffect(() => {
        const updateGuidelinePosition = () => {
            const chatInput = document.querySelector('.chat-input-container')
            const messageList = messageContainerRef.current
            if (guidelineWrapperRef.current && chatInput && messageList) {
                const inputHeight = chatInput.offsetHeight
                const guidelineHeight = guidelineWrapperRef.current.offsetHeight + 21
                guidelineWrapperRef.current.style.bottom = `${
                    inputHeight + 10
                }px`
                messageList.style.paddingBottom = `${guidelineHeight}px`
            }
        }

        if (isMobile) {
            updateGuidelinePosition()
            const resizeObserver = new ResizeObserver(updateGuidelinePosition)
            const chatInput = document.querySelector('.chat-input-container')
            if (chatInput) {
                resizeObserver.observe(chatInput)
            }

            return () => {
                if (chatInput) {
                    resizeObserver.unobserve(chatInput)
                }
                if (messageContainerRef.current) {
                    messageContainerRef.current.style.paddingBottom = ''
                }
            }
        }
    }, [isMobile, messages, loadingChat])

    const handleFeedbackChange = () => {
        setTimeout(scrollToBottom, 100)
    }

   const { MAP_LENDER_ID } = programs.data;
   const { MAP_LENDER_CONTACT } = contact.data;

    const LENDER_CONTACT = useMemo(
        () =>
            Object.fromEntries(
            Object.keys(MAP_LENDER_ID).map((k) => [
                k,
                {
                ...MAP_LENDER_ID[k],
                contacts: MAP_LENDER_CONTACT[k],
                },
            ]),
            ),
        [MAP_LENDER_ID, MAP_LENDER_CONTACT],
    );

    const SCENARIO_AI_USE = useScenarioUsed();

    const ACTIVE_GP_TMODE = sessionStorage.getItem('activeGPTmode');

    // 是否需要停止用户继续咨询、并警告
    const STOP_ASKING = (function(){
        if(IS_WHITE_LIST) return false;
        switch(sessionStorage.getItem('activeGPTmode')){
            case 'guideline':
                // Agency guidelines 走老的逻辑
                return isLandingWelcome;
            case 'scenario':
                // Scenario AI 付费不限次数，未付费不得超过3次
                return SCENARIO_AI_USE.DISABLED;
            default: return true;
        }
    })();

    const { MAP_GUIDELINE_ID, GUIDELINES_ID } = programs.data;
    // 目前上下文展示对应的，后续每段话都有关联的
    const LENDER_IDS = useMemo(() => {
       const sources = previewId ? GUIDELINES_ID : source
       return [...new Set(sources.map(k=> MAP_GUIDELINE_ID?.[k]?.lenderId))];
    }, [source, previewId, MAP_GUIDELINE_ID, GUIDELINES_ID]);
 
    if (loadingChat) {
        return (
            <div className="message-list pt-3">
                <Skeleton active />
                <Skeleton active />
                <Skeleton active />
            </div>
        )
    }

    const IS_NONQM = window.location.pathname.includes('/app/nonqmcalculator');
    const MSG_LENGTH = messages.length;

    return (
        <>
            <div
                className="message-list"
                ref={(el) => {
                    messageContainerRef.current = el
                    setContainerElement(el)
                }}
            >
                {messages.map(({ text, sender, isProgress, thinkingStartDate, thinkingEndDate, thinking }, index) => (
                    <Message
                        key={index}
                        text={text}
                        index={index}
                        sender={sender}
                        previewId={previewId}
                        programs={programs}
                        currentPdfType={pdfType}
                        pdCFontent={pdCFontent}
                        isMobile={isMobile}
                        isScenario={isScenario}
                        source={source}
                        isProgress={isProgress}
                        thinking={thinking}
                        onContinue={onContinue}
                        onToSignUp={onToSignUp}
                        onSuccess={onSuccess}
                        thinkingEndDate={thinkingEndDate}
                        thinkingStartDate={thinkingStartDate}
                        ref={isLastUserMessage(index) ? lastQuestionRef : null}
                        isFannieMaeStandalone={isFannieMaeStandalone}
                        isFreddieMacStandalone={isFreddieMacStandalone}
                        signupGuide={MSG_LENGTH === index + 1 && sender === 'bot' && signupGuide}
                    />
                ))}
                {isBotThinking && !messages.some(msg => msg.isProgress) && (
                    <Message text="..." sender="bot" isMobile={isMobile} />
                )}
                {isProcessing && progressMessage && !messages.some(msg => msg.isProgress) && (
                    <Message 
                        text={progressMessage} 
                        sender="bot" 
                        isMobile={isMobile}
                        isProgress={true}
                    />
                )}
                {signupGuide ? null : <ChatFeedback
                    show={showFeedback}
                    threadID={threadID}
                    chatAnswer={currentMessageText}
                    messageID={currentMessageID}
                    onFeedbackChange={handleFeedbackChange}
                >
                    {(ACTIVE_GP_TMODE === 'scenario' && LENDER_IDS?.length && !IS_NONQM && SHOW_CONTACT) ? <ContactRelation LENDER_CONTACT={LENDER_CONTACT} lenderIds={LENDER_IDS} /> : <span/>}
                </ChatFeedback>}
            </div>
            {/* Text Selection Popup */}
            {STOP_ASKING || <TextSelectionPopup
                selectedText={selectedText}
                position={popupPosition}
                onExplain={handleExplain}
                onClose={closePopup}
                visible={showPopup}
            />}
            {isMobile && (
                <div
                    className="guideline-floating-wrapper"
                    ref={guidelineWrapperRef}
                >
                    <div
                        className="floating-guideline-button"
                        onClick={() => setMobileAIChat(false)}
                    >
                        <img src="/images/guideline.svg" alt="Guideline" />
                        <span>Guideline</span>
                    </div>
                </div>
            )}
        </>
    )
}

export default MessageList
export { Message }
